import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'
import parse from 'html-react-parser'
import { FormProvider, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'next-i18next/pages'
import { MeterDetailsInputs, PreviousSupplier } from 'store/customer/types'
import { InputGroupKeys, MeterDetailsFieldKeys, SimulationFieldKeys } from 'store/customer/enums'
import { DATE_FORMAT, DATE_FORMAT_ISO, MONTHS } from '../../constants'
import { checkDynamicTariffEligible, checkSetllementFrequencyEligible, dateToString, getStartDateRange } from '../../utils'
import FormButtons from '../../components/form-buttons/FormButtons'
import { useRouter } from 'next/router'
import { useBeforeunload } from 'react-beforeunload'
import { StepProps } from '../types'
import { checkHasSolarKva, determineSimulationUsage, requestAndSavePropositions } from 'features/simulation/utils'
import { selectProposition, updateAllInputs, updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { checkCanApplyPlentyPromo, getProductName, getUsableProducts } from 'utils/products'
import { trackEvent, trackGTMEvent } from 'utils/tracking'
import {
  Button,
  Checkbox,
  DatePickerNative,
  Form,
  Icon,
  InlineMessage,
  Modal,
  Radio,
  RadioList,
  Select,
  TextField,
  Toggle,
  ToggleRadio,
  Tooltip
} from '@boltenergy-be/design-system'
import Link from 'components/link/Link'
import { routes } from 'utils/routes'
import { log, setLogContext } from 'utils/logging'
import { Criticality, Flow } from 'types/logging'
import { Address, Ean, Email, FirstName, LastName, MeterNumber, MeterReading } from 'components/inputs'
import { EanType } from 'components/inputs/ean/types'
import CheckboxList from 'components/checkbox-list/CheckboxList'
import { selectIsBoltDriveFlexFlow } from 'features/bolt-drive-flex/selectors'
import { setPromoOrReferralCodeInStore } from 'utils/customerFlow'
import {
  COUNTER_TYPES,
  CounterType,
  CUSTOMER_TYPES,
  DEFAULT_PRODUCT,
  EAN_CAPTURE_TYPES,
  ENERGY_SUPPLIERS,
  ENERGY_TYPES,
  METER_TYPES,
  PLENTY_PRODUCTS,
  Product,
  REGIONS,
  SETTLEMENT_FREQUENCIES,
  SettlementFrequency,
  SIMULATION_TYPES,
  SimulationType,
  Situation,
  SITUATIONS,
  USAGE_GRADES,
  UTILITIES_ERROR_CODES
} from '@boltenergy-be/shared'
import boltBackend from 'lib/bolt-backend'
import { useFeatureFlagTranslatedPayload } from 'hooks/useFeatureFlagTranslatedPayload'
import { FEATURE_FLAGS } from 'constants/tracking'
import { checkCanPassIndexesAndMeterNumbers } from 'lib/bolt-backend/registrations/registrations.utils'
import { CommonTrackingParams } from 'types/tracking'
import FileUpload from 'components/inputs/file-upload/FileUpload'
import { postCreditCheckScreening } from 'lib/bolt-backend/resources/prospects/creditCheckScreening'
import { getProductPromoForDate } from 'components/promo-code/utils'

const MeterDetails = ({ nextStep, saveAndResimulate }: StepProps) => {
  // Redux store
  const { opportunityId } = useStoreSelector((store) => store.app)
  const { inputs, propositions } = useStoreSelector((store) => store.customer)
  const marketingStore = useStoreSelector((store) => store.marketing)
  const {
    adjustableProduct,
    eanCodeMandatory,
    eventID,
    sourceType,
    previousSupplier,
    availableProducts,
    dynamicTariffDefaultChecked,
    settlementFrequencyMonthlyByDefault,
    identificationMethod
  } = marketingStore
  const { counterType, dynamicTariff, hasSolarPanels, walloniaSolarPanelsSince2024, ...otherMeterDetails } =
    inputs[InputGroupKeys.METER_DETAILS]
  const { meterType, hasExclusiveNightMeter, usage, region, chosenSimulationType } = inputs[InputGroupKeys.SIMULATION]
  const { isCompany } = inputs[InputGroupKeys.PERSONAL_DATA]
  const { selectedProposition } = propositions
  const dispatch = useStoreDispatch()

  // Next router
  const { locale } = useRouter()

  // i18n
  const { t } = useTranslation(['registration', 'validation', 'common', 'simulation', 'commonFormFields'])

  // Day.js
  dayjs.extend(customParseFormat)
  dayjs.extend(isBetween)

  // Posthog
  const knowsMeterDetailsLabelFlag = useFeatureFlagTranslatedPayload({
    key: FEATURE_FLAGS.knowsEanLabelText,
    fallback: t('meterDetails.fields.knowsMeterDetails.label')
  })

  // React Hook Form
  const hookform = useForm<Omit<MeterDetailsInputs, 'hasSolarPanels' | 'dynamicTariff'>>({
    defaultValues: {
      ...(usage.grade !== USAGE_GRADES.custom && {
        counterType
      }),
      ...otherMeterDetails
    },
    mode: 'onBlur'
  })
  const {
    formState: { errors, isDirty },
    handleSubmit,
    register,
    setValue,
    watch
  } = hookform

  // React hook form watch values (watches those fields & re-renders if they have been changed - for dynamic form purposes)
  const watchContractStartDate = watch('contractStartDate')
  const watchCounterType: CounterType = watch(MeterDetailsFieldKeys.COUNTER_TYPE) as CounterType
  const watchKnowsMeterDetails: boolean = watch('knowsMeterDetails')
  const watchSituation: Situation = watch(MeterDetailsFieldKeys.SITUATION) as Situation
  const watchSupplier: PreviousSupplier | undefined = watch('previousSupplier')
  const watchSettlementFrequency = watch('settlementFrequency') as SettlementFrequency

  // Constants
  const counterTypeToCheck = usage.grade === USAGE_GRADES.custom ? counterType : watchCounterType
  const { min, max, initial } = getStartDateRange(watchSituation)
  const daysFromStartDate = dayjs(watchContractStartDate).diff(Date.now(), 'd')
  const isMeterReadingMonthVisible =
    checkHasSolarKva({
      counterType: inputs.meterDetails.counterType,
      hasSolarPanels: inputs.meterDetails.hasSolarPanels,
      walloniaSolarPanelsSince2024: inputs.meterDetails.walloniaSolarPanelsSince2024
    }) && watchSituation === SITUATIONS.switch
  const isMove = watchSituation === SITUATIONS.move
  const showMeterReadAndNumberInputs = checkCanPassIndexesAndMeterNumbers(watchSituation, counterTypeToCheck, watchContractStartDate)
  const skipReSimulate = ([SIMULATION_TYPES.eav, SIMULATION_TYPES.fixedAmount] as SimulationType[]).includes(chosenSimulationType)
  const usableProducts = adjustableProduct
    ? getUsableProducts({
        region: inputs.simulation.region,
        withPlenty: availableProducts?.[region]?.some((product) => PLENTY_PRODUCTS.some((plentyProduct) => plentyProduct === product))
      })
    : undefined
  const isBoltDriveFlexFlow = selectIsBoltDriveFlexFlow(marketingStore)
  const canToggleDynamicTariff = checkDynamicTariffEligible({
    proposition: selectedProposition,
    counterType: counterTypeToCheck,
    region,
    hasExclusiveNightMeter
  })
  const canToggleSettlementFrequency = checkSetllementFrequencyEligible({ eventID, region, counterType: counterTypeToCheck })

  // Local state
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadingAdjustableProduct, setLoadingAdjustableProduct] = useState<boolean>(false)
  const [showContractStartDatePicker, setShowContractStartDatePicker] = useState<boolean>(
    inputs.meterDetails.contractStartDate && !dayjs(inputs.meterDetails.contractStartDate).isSame(initial, 'day')
  )
  const [hasExclNightEan, setHasExclNightEan] = useState<boolean>(
    !!inputs[InputGroupKeys.METER_DETAILS][MeterDetailsFieldKeys.ELECTRICITY].eanExclNight
  )
  const [switchDate, setSwitchDate] = useState<string>('')
  const [gasEanAtElectricityEanField, setGasEanAtElectricityEanField] = useState<boolean>(false)
  const [electricityEanAtGasEanField, setElectricityEanAtGasEanField] = useState<boolean>(false)
  const [invalidEan, setInvalidEan] = useState<{ el: boolean; gas: boolean }>({ el: false, gas: false })
  const [showSettlementFrequencyModal, setShowSettlementFrequencyModal] = useState<boolean>(false)
  const [settlementFrequency, setSettlementFrequency] = useState<SettlementFrequency>(
    otherMeterDetails[MeterDetailsFieldKeys.SETTLEMENT_FREQUENCY] || SETTLEMENT_FREQUENCIES.yearly
  )

  // Date constants
  const maxDate = switchDate && dayjs(switchDate).isAfter(max) ? switchDate : max
  const minDate = switchDate && dayjs(switchDate).isBefore(min) ? switchDate : min

  /**
   * Removes the previous owner fields if there is a source type and the situation is not move
   */
  useEffect(() => {
    if (sourceType && !isMove) {
      setValue(MeterDetailsFieldKeys.PREVIOUS_INHABITANT, undefined)
    }
  }, [sourceType, isMove])

  // Set the default value for settlement frequency if not set yet. Has to happen in useEffect because of counterTypesToCheck logic
  useEffect(() => {
    if (!watchSettlementFrequency) {
      if (canToggleSettlementFrequency && settlementFrequencyMonthlyByDefault) {
        setSettlementFrequency(SETTLEMENT_FREQUENCIES.monthly)
        setValue(MeterDetailsFieldKeys.SETTLEMENT_FREQUENCY, SETTLEMENT_FREQUENCIES.monthly)
      } else {
        setValue(MeterDetailsFieldKeys.SETTLEMENT_FREQUENCY, SETTLEMENT_FREQUENCIES.yearly)
      }
    }
  }, [watchSettlementFrequency, canToggleSettlementFrequency, settlementFrequencyMonthlyByDefault])

  // Set the default value for dynamic tariff. Has to happen in useEffect because of counterTypesToCheck logic
  useEffect(() => {
    if (typeof dynamicTariff !== 'boolean' && canToggleDynamicTariff && dynamicTariffDefaultChecked) {
      saveAndResimulate(InputGroupKeys.METER_DETAILS, MeterDetailsFieldKeys.DYNAMIC_TARIFF, true, skipReSimulate || !hasSolarPanels)
    }
  }, [dynamicTariff, canToggleDynamicTariff, dynamicTariffDefaultChecked])

  /**
   * Prevents unload if one of the form fields is changed
   */
  useBeforeunload((e) => {
    if (isDirty) e.preventDefault()
  })

  /**
   * Checks if the given contract start date is between the min & max allowed dates
   *
   * @param {Date} contractStartDate
   * @returns {boolean}
   */
  const contractStartDateIsBetweenMinAndMax = (contractStartDate: string): boolean => {
    return dayjs(contractStartDate).isBetween(minDate, maxDate, 'day', '[]')
  }

  /**
   * Handles the Counter Type change
   *
   * @param {CounterType} value
   */
  const handleCounterTypeChange = (value: CounterType) => {
    // Reset dynamic tariff if counter type is changed to analog
    if (value === COUNTER_TYPES.analog) {
      // Monthly is only possible when the meter is digital so make sure to reset to yearly
      setSettlementFrequency(SETTLEMENT_FREQUENCIES.yearly)
      setValue(MeterDetailsFieldKeys.SETTLEMENT_FREQUENCY, SETTLEMENT_FREQUENCIES.yearly)

      if (region === REGIONS.brussels && hasSolarPanels) {
        // Set solar panels to false --> brussel + solar panels + analog is not possible
        dispatch(
          updateInput({
            group: InputGroupKeys.METER_DETAILS,
            key: MeterDetailsFieldKeys.HAS_SOLAR_PANELS,
            value: false
          })
        )
      }

      if (region === REGIONS.wallonia) {
        // Set value of walloniaSolarPanelsSince2024 to false --> prevent impossible combination
        dispatch(
          updateInput({
            group: InputGroupKeys.METER_DETAILS,
            key: MeterDetailsFieldKeys.WALLONIA_SOLAR_PANELS_SINCE_2024,
            value: false
          })
        )
      }

      // Set the value without re-simulating because a resimulation will be done at the end of the function
      dispatch(updateInput({ group: InputGroupKeys.METER_DETAILS, key: MeterDetailsFieldKeys.DYNAMIC_TARIFF, value: false }))
    }

    if ((dynamicTariffDefaultChecked || settlementFrequencyMonthlyByDefault) && value === COUNTER_TYPES.digital) {
      const dynamicTariffTogglable = checkDynamicTariffEligible({
        proposition: selectedProposition,
        counterType: value,
        region,
        hasExclusiveNightMeter
      })
      if (dynamicTariffDefaultChecked && dynamicTariffTogglable) {
        dispatch(updateInput({ group: InputGroupKeys.METER_DETAILS, key: MeterDetailsFieldKeys.DYNAMIC_TARIFF, value: true }))
      }

      const settlementFrequencyTogglable = checkSetllementFrequencyEligible({ eventID, region, counterType: value })
      if (settlementFrequencyMonthlyByDefault && settlementFrequencyTogglable) {
        setSettlementFrequency(SETTLEMENT_FREQUENCIES.monthly)
        setValue(MeterDetailsFieldKeys.SETTLEMENT_FREQUENCY, SETTLEMENT_FREQUENCIES.monthly)
      }
    }

    saveAndResimulate(InputGroupKeys.METER_DETAILS, MeterDetailsFieldKeys.COUNTER_TYPE, value, skipReSimulate || !hasSolarPanels)
  }

  /**
   * Handles the meter details checkbox change event
   *
   * @param {InputGroupKeys} group
   * @param {SimulationFieldKeys | MeterDetailsFieldKeys} key
   * @param {unknown} value
   */
  const handleMeterDetailsCheckboxChanged = (group: InputGroupKeys, key: SimulationFieldKeys | MeterDetailsFieldKeys, value: unknown) => {
    // Update the value in the store
    dispatch(updateInput({ group, key, value }))

    // Update the simulation usage
    dispatch(
      updateInput({
        group: InputGroupKeys.SIMULATION,
        key: SimulationFieldKeys.USAGE,
        value: determineSimulationUsage()
      })
    )
  }

  /**
   * Handles the meter reading month select
   *
   * @param {number} selectedMonthIndex
   */
  const handleMeterReadingMonthSelectChange = (selectedMonthIndex: number): void => {
    if (!isNaN(selectedMonthIndex)) {
      // Define the start date
      let startDate = dayjs()
        .month(selectedMonthIndex - 1)
        .date(15)
        .hour(12)

      // Check if start date is in the future and add a year if not
      if (startDate.isBefore(dayjs())) startDate = startDate.add(1, 'year')

      // Show the contract start date picker
      setShowContractStartDatePicker(true)

      // Set the value in RHF values
      setValue(MeterDetailsFieldKeys.CONTRACT_START_DATE, startDate.toISOString())
    }
  }

  /**
   * Handles the situation change & sets the new contract start date
   *
   * @param {Situation} situation
   */
  const handleSituationChange = (situation: Situation) => {
    setValue(MeterDetailsFieldKeys.CONTRACT_START_DATE, getStartDateRange(situation).initial)
  }

  /**
   * Handles the submit after validation
   *
   * @param {MeterDetailsInputs} data
   */
  const onSubmit = async (data: MeterDetailsInputs) => {
    const startTime = performance.now()
    try {
      setIsLoading(true)
      setLogContext('meter_details_form_input', data)

      // Only check EAN data if the user knows the meter details
      if (data.knowsMeterDetails) {
        // Fetch the EAN data
        const [electricityEanDataResponse, gasEanDataResponse] = await Promise.all([
          boltBackend.getEanData({
            contractStartDate: data.contractStartDate,
            ean: data.electricity.ean.toString(),
            energyType: ENERGY_TYPES.electricity
          }),
          ...(inputs.deliveryAddress.needsGas && data.gas.ean
            ? [
                boltBackend.getEanData({
                  contractStartDate: data.contractStartDate,
                  ean: data.gas.ean.toString(),
                  energyType: ENERGY_TYPES.gas
                })
              ]
            : [])
        ])

        // Four scenarios to be blocked
        // 1. Gas EAN filled in at electricity
        const gasEanAtElec = electricityEanDataResponse?.data?.energyType === ENERGY_TYPES.gas
        // 2. Electricity EAN filled in at gas
        const elecEanAtGas = gasEanDataResponse?.data?.energyType === ENERGY_TYPES.electricity
        // 3. Invalid contract start date & no document uploaded
        const contractStartDateIsInvalid =
          electricityEanDataResponse?.data?.invalidContractStartDate && (!data.upload?.eod || data.upload?.eod?.length === 0)
        const invalidEanCodeFromBackend = [gasEanDataResponse?.errorCode, electricityEanDataResponse?.errorCode].includes(
          UTILITIES_ERROR_CODES.eanNotFoundOnMarket
        )

        setInvalidEan({
          el: electricityEanDataResponse?.errorCode === UTILITIES_ERROR_CODES.eanNotFoundOnMarket,
          gas: gasEanDataResponse?.errorCode === UTILITIES_ERROR_CODES.eanNotFoundOnMarket
        })
        setGasEanAtElectricityEanField(gasEanAtElec)
        setElectricityEanAtGasEanField(elecEanAtGas)
        if (contractStartDateIsInvalid) {
          setSwitchDate(electricityEanDataResponse?.data?.switchDate)
          handleSituationChange(SITUATIONS.move)
          setValue(MeterDetailsFieldKeys.SITUATION, SITUATIONS.move)
          // Scroll to top (where form is located)
          window.scrollTo(0, 0)
        }

        if (!gasEanAtElec && !elecEanAtGas && !contractStartDateIsInvalid && !invalidEanCodeFromBackend) {
          const durationUntilNow = performance.now() - startTime
          saveDataAndGoToNextStep(data, durationUntilNow)
        }
      } else {
        const durationUntilNow = performance.now() - startTime
        saveDataAndGoToNextStep(data, durationUntilNow)
      }
    } catch (error) {
      log({ error, identifier: `[${Flow.REGISTRATION}:createCustomer:meterDetails]`, criticality: Criticality.HIGH })
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Fires the credit check screening call if there is a opportunityId.
   * @param {MeterDetailsInputs} data
   */
  const fireCreditCheckScreening = (data: MeterDetailsInputs) => {
    if (opportunityId) {
      const { firstName, lastName, email, mobilePhone, companyNumber } = inputs[InputGroupKeys.PERSONAL_DATA]
      postCreditCheckScreening(opportunityId, {
        firstName,
        lastName,
        email,
        phone: mobilePhone,
        enterpriseNumber: companyNumber || undefined,
        eans:
          data.electricity.ean && watchKnowsMeterDetails
            ? {
                electricity: String(data.electricity.ean),
                gas: inputs.deliveryAddress.needsGas && data.gas?.ean ? String(data.gas.ean) : undefined
              }
            : undefined
      })
    }
  }

  /**
   * Saves the data & goes to the next step
   * @param {MeterDetailsInputs} data
   * @param {number} durationUntilNow
   */
  const saveDataAndGoToNextStep = (data: MeterDetailsInputs, durationUntilNow: number) => {
    const startTime = performance.now()
    fireCreditCheckScreening(data)

    // Delete upload if situation is not move or if situation is move but no doc is selected
    if (data.situation !== SITUATIONS.move || (data.situation === SITUATIONS.move && data.upload?.eod?.length === 0)) {
      delete data?.upload?.eod
    }

    // Update the fields in the store
    dispatch(
      updateAllInputs({
        inputs: {
          ...inputs,
          meterDetails: {
            ...inputs.meterDetails,
            ...data,
            contractStartDate: dayjs(data.contractStartDate).toISOString()
          }
        }
      })
    )

    const totalDuration = performance.now() - startTime + durationUntilNow
    // Track 'Subscription Step Finished' event @Semetis
    trackGTMEvent('subscription_step_finished:meter-details', { duration_ms: totalDuration })
    trackEvent(
      { category: 'subscription', object: 'meter_details_step', action: 'finish' },
      { [CommonTrackingParams.IDENTIFICATION_METHOD]: identificationMethod, [CommonTrackingParams.DURATION]: totalDuration }
    )

    // Go to the next step
    nextStep()
  }

  /**
   * Updates the Ean Capture Type input to "Overwritten by customer"
   */
  const updateEanCaptureTypeToOverwrittenIfNecessary = () => {
    if (inputs.meterDetails.eanCaptureType === EAN_CAPTURE_TYPES.prefilled) {
      dispatch(
        updateInput({
          group: InputGroupKeys.METER_DETAILS,
          key: MeterDetailsFieldKeys.EAN_CAPTURE_TYPE,
          value: EAN_CAPTURE_TYPES.overwritten
        })
      )
    }
  }

  return (
    <FormProvider {...hookform}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal
          isOpen={showSettlementFrequencyModal}
          onRequestClose={() => setShowSettlementFrequencyModal(false)}
          title={t('meterDetails.yearlyOrMonthlyModal.title')}
        >
          <p>{t('meterDetails.yearlyOrMonthlyModal.description1')}</p>
          <p>{parse(t('meterDetails.yearlyOrMonthlyModal.description2'))}</p>
          <p>{t('meterDetails.yearlyOrMonthlyModal.description3')}</p>
          <ToggleRadio
            label={t('meterDetails.yearlyOrMonthlyModal.howOftenField.label')}
            name={MeterDetailsFieldKeys.SETTLEMENT_FREQUENCY}
            id={MeterDetailsFieldKeys.SETTLEMENT_FREQUENCY}
            options={[
              {
                id: SETTLEMENT_FREQUENCIES.yearly,
                label: t('common:periodicity.yearly'),
                value: SETTLEMENT_FREQUENCIES.yearly,
                onChange: (event) => setSettlementFrequency(event.target.value as SettlementFrequency),
                checked: settlementFrequency === SETTLEMENT_FREQUENCIES.yearly
              },
              {
                id: SETTLEMENT_FREQUENCIES.monthly,
                label: t('common:periodicity.monthly'),
                value: SETTLEMENT_FREQUENCIES.monthly,
                onChange: (event) => setSettlementFrequency(event.target.value as SettlementFrequency),
                checked: settlementFrequency === SETTLEMENT_FREQUENCIES.monthly
              }
            ]}
          />
          <Button
            onClick={() => {
              setShowSettlementFrequencyModal(false)
              setValue(MeterDetailsFieldKeys.SETTLEMENT_FREQUENCY, settlementFrequency)
            }}
          >
            {t('meterDetails.yearlyOrMonthlyModal.howOftenField.confirm')}
          </Button>
        </Modal>

        {/* SITUATION */}
        <Form.Fieldset title={t('meterDetails.sections.situation')}>
          <RadioList label="" grid="full">
            <Radio
              {...register(MeterDetailsFieldKeys.SITUATION, { onChange: () => handleSituationChange(SITUATIONS.switch) })}
              id={SITUATIONS.switch}
              label={t('meterDetails.fields.situation.options.switch')}
              value={SITUATIONS.switch}
            />
            <Radio
              {...register(MeterDetailsFieldKeys.SITUATION, { onChange: () => handleSituationChange(SITUATIONS.move) })}
              id={SITUATIONS.move}
              label={t('meterDetails.fields.situation.options.move')}
              value={SITUATIONS.move}
            />
            <Radio
              {...register(MeterDetailsFieldKeys.SITUATION, { onChange: () => handleSituationChange(SITUATIONS.newConnection) })}
              id={SITUATIONS.newConnection}
              label={t('meterDetails.fields.situation.options.newConnection')}
              value={SITUATIONS.newConnection}
            />
          </RadioList>
        </Form.Fieldset>

        {/* PREVIOUS SUPPLIER */}
        {previousSupplier && watchSituation !== SITUATIONS.newConnection && (
          <Form.Fieldset title="">
            <Select {...register('previousSupplier.supplier')} label={t('meterDetails.fields.previousSupplier.label')}>
              <option value="">{t('commonFormFields:select')}</option>
              {Object.keys(ENERGY_SUPPLIERS)
                .filter((supplier) => ENERGY_SUPPLIERS[supplier] !== ENERGY_SUPPLIERS.other)
                .map((supplier) => (
                  <option key={supplier} value={ENERGY_SUPPLIERS[supplier]}>
                    {ENERGY_SUPPLIERS[supplier]}
                  </option>
                ))}
              <option value={ENERGY_SUPPLIERS.other}>{t('meterDetails.fields.previousSupplier.other.label')}</option>
            </Select>

            {watchSupplier?.supplier === ENERGY_SUPPLIERS.other && (
              <TextField
                {...register('previousSupplier.otherSupplier', {
                  maxLength: {
                    value: 48,
                    message: t('validation:max.string', {
                      attribute: t('meterDetails.fields.previousSupplier.other.placeholder'),
                      length: 48
                    })
                  },
                  required: {
                    message: t('validation:required'),
                    value: watchSupplier.supplier === ENERGY_SUPPLIERS.other
                  }
                })}
                error={errors.previousSupplier?.otherSupplier?.message as string}
                label={t('meterDetails.fields.previousSupplier.other.placeholder')}
              />
            )}
          </Form.Fieldset>
        )}

        {/* METER READING MONTH */}
        {isMeterReadingMonthVisible && (
          <Form.Fieldset title={t('meterDetails.fields.meteringMonth.label')}>
            <Select
              {...register(MeterDetailsFieldKeys.METER_READING_MONTH, {
                onChange: (e) => handleMeterReadingMonthSelectChange(parseInt(e.currentTarget.value)),
                required: t('validation:required')
              })}
              label=""
            >
              <option value="" disabled>
                {t('commonFormFields:select')}
              </option>
              {MONTHS.map((month, index) => (
                <option key={index} value={index + 1}>
                  {t(`meterDetails.fields.meteringMonth.options.${month.i18nKey}`, month.defaultValue)}
                </option>
              ))}
            </Select>

            <InlineMessage type="informative" className="grid-col-full">
              {t('meterDetails.fields.meteringMonth.description')}
            </InlineMessage>
          </Form.Fieldset>
        )}

        {/* CONTRACT START DATE */}
        <Form.Fieldset title={t('meterDetails.fields.contractStartDate.label')}>
          {watchSituation === SITUATIONS.newConnection ? (
            <InlineMessage type="informative" className="grid-col-full">
              {t('meterDetails.fields.contractStartDate.newConnectionActivation')}
            </InlineMessage>
          ) : (
            <>
              {!showContractStartDatePicker && (
                <p className="grid-col-full">
                  {parse(
                    t('meterDetails.fields.contractStartDate.description', {
                      max: dateToString(initial)
                    })
                  )}
                  &nbsp;
                  <Button type="button" representation="link" onClick={() => setShowContractStartDatePicker(true)}>
                    {t('common:change')}
                  </Button>
                </p>
              )}

              {showContractStartDatePicker && (
                <>
                  <DatePickerNative
                    {...register('contractStartDate', {
                      required: t('validation:required'),
                      validate: (value) =>
                        contractStartDateIsBetweenMinAndMax(value) ||
                        t('meterDetails.fields.contractStartDate.errors.invalid', {
                          max: dayjs(maxDate).format('DD/MM/YYYY'),
                          min: dayjs(minDate).format('DD/MM/YYYY')
                        })
                    })}
                    error={errors?.contractStartDate?.message}
                    label={t('meterDetails.fields.contractStartDate.desiredContractStartDate')}
                    labelComponentSlot={
                      <Tooltip
                        tooltipContent={t('meterDetails.fields.contractStartDate.range', {
                          max: dateToString(maxDate),
                          min: dateToString(minDate)
                        })}
                      >
                        <Icon name="questionMarkCircle" />
                      </Tooltip>
                    }
                    max={dayjs(maxDate).format(DATE_FORMAT_ISO)}
                    min={dayjs(minDate).format(DATE_FORMAT_ISO)}
                    value={dayjs(watch('contractStartDate')).format(DATE_FORMAT_ISO)}
                  />

                  <Button
                    className="grid-col-full"
                    type="button"
                    representation="link"
                    onClick={() => {
                      setShowContractStartDatePicker(false)
                      setValue(MeterDetailsFieldKeys.CONTRACT_START_DATE, initial) // Reset contract start date back to initial
                    }}
                  >
                    {t('meterDetails.fields.contractStartDate.useDefault')}
                  </Button>
                </>
              )}

              {watchSituation === SITUATIONS.move && daysFromStartDate < 0 && (
                <InlineMessage className="grid-col-full" type="informative">
                  {t('meterDetails.fields.contractStartDate.info.moveInPast')}
                </InlineMessage>
              )}

              {watchSituation === SITUATIONS.move && daysFromStartDate <= 21 && daysFromStartDate >= 0 && (
                <InlineMessage className="grid-col-full" type="informative">
                  {t('meterDetails.fields.contractStartDate.info.moveThreshold')}
                </InlineMessage>
              )}

              {watchSituation === SITUATIONS.switch && isMeterReadingMonthVisible && !!watchContractStartDate && (
                <InlineMessage className="grid-col-full" type="informative">
                  {t('meterDetails.fields.contractStartDate.info.startDateChangedMeteringMonth')}
                </InlineMessage>
              )}

              {switchDate !== '' && switchDate !== watchContractStartDate && sourceType && watchSituation === SITUATIONS.move ? (
                <InlineMessage className="grid-col-full" type="negative">
                  {t('meterDetails.sections.previousInhabitant.warning', { date: dayjs(switchDate).format('DD/MM/YYYY') })}
                </InlineMessage>
              ) : (
                switchDate !== '' &&
                switchDate !== watchContractStartDate && (
                  <InlineMessage className="grid-col-full" type="negative">
                    {t('meterDetails.fields.contractStartDate.gap', { date: dayjs(switchDate).format(DATE_FORMAT) })}
                  </InlineMessage>
                )
              )}
            </>
          )}
        </Form.Fieldset>

        {/* PREVIOUS INHABITANT FORM */}
        {switchDate !== '' && switchDate && switchDate !== watchContractStartDate && sourceType && watchSituation === SITUATIONS.move && (
          <Form.Fieldset title={t('meterDetails.sections.previousInhabitant.title')}>
            <p className="grid-col-full">{t('registration:meterDetails.sections.previousInhabitant.description')}</p>

            <FirstName parentDotPath="previousInhabitant" required={false} />

            <LastName parentDotPath="previousInhabitant" required={false} />

            <Address parentDotPath="previousInhabitant.address" required={false} />

            <TextField
              {...register('previousInhabitant.supplier')}
              error={errors?.previousInhabitant?.supplier?.message as string}
              label={t('commonFormFields:supplier.label')}
            />

            <Select {...register('previousInhabitant.clientType')} label={t('commonFormFields:addressType.label')}>
              <option value={CUSTOMER_TYPES.residential}>{t('common:residential')}</option>
              <option value={CUSTOMER_TYPES.professional}>{t('common:professional')}</option>
            </Select>

            <Email parentDotPath="previousInhabitant" required={false} />
          </Form.Fieldset>
        )}

        {/* ENERGY TRANSITION DOCUMENT*/}
        {isMove && (
          <Form.Fieldset title={t('meterDetails.sections.upload.eod.label')}>
            <FileUpload
              identifier="eod"
              parentDotPath="upload"
              message={{
                type: 'informative',
                text: (
                  <Trans
                    t={t}
                    i18nKey="meterDetails.sections.upload.eod.description"
                    components={[
                      <Link
                        as="a"
                        variant="inline"
                        href="/nl/encyclopedie/energie-thuis/regel-energiecontract-voor-verhuis/energieovernamedocument-nodig"
                        target="_blank"
                      />
                    ]}
                  />
                )
              }}
            />
          </Form.Fieldset>
        )}

        {sourceType.toLowerCase() === 'push' && (
          <Form.Fieldset title={t('meterDetails.sections.upload.contract.label')}>
            <FileUpload identifier="contract" parentDotPath="upload" />
          </Form.Fieldset>
        )}

        {adjustableProduct && !!usableProducts?.length && (
          <Form.Fieldset title={t('meterDetails.fields.product.label')}>
            <Select
              loading={loadingAdjustableProduct}
              defaultValue={selectedProposition || availableProducts?.[region]?.[0] || DEFAULT_PRODUCT}
              label=""
              name="product"
              onChange={async (e) => {
                const product = e.currentTarget.value as Product
                let validPromoCode = false

                dispatch(selectProposition({ productType: product }))

                try {
                  setLoadingAdjustableProduct(true)

                  // Check if the selected product is a Plenty product and if the promo code can be applied
                  if (checkCanApplyPlentyPromo(product)) {
                    const currentPromo = getProductPromoForDate(product)
                    const { valid } = await setPromoOrReferralCodeInStore(
                      isCompany ? currentPromo?.code[CUSTOMER_TYPES.professional] : currentPromo?.code[CUSTOMER_TYPES.residential]
                    )
                    validPromoCode = valid
                  }

                  // Trigger re-simulation if the selected product is not already simulated or if a valid promo code is applied
                  if (!skipReSimulate && (!propositions?.simulatedPropositions?.[product] || validPromoCode)) {
                    requestAndSavePropositions({ products: usableProducts })
                  }
                } finally {
                  setLoadingAdjustableProduct(false)
                }
              }}
            >
              {usableProducts.map((product) => (
                <option key={product} value={product}>
                  {getProductName(product, locale)}
                </option>
              ))}
            </Select>

            <Form.EmptyField />
          </Form.Fieldset>
        )}

        {usage.grade !== USAGE_GRADES.custom && (
          <>
            {/* SOLAR PANELS */}
            <Form.Fieldset title={t('meterDetails.fields.solarPanels.label')}>
              <div className="grid-col-full">
                <Toggle
                  active={!!hasSolarPanels}
                  id="hasSolarPanels"
                  isFullwidth
                  disabled={region === REGIONS.brussels && counterTypeToCheck === COUNTER_TYPES.analog}
                  onClick={(value) => {
                    saveAndResimulate(InputGroupKeys.METER_DETAILS, MeterDetailsFieldKeys.HAS_SOLAR_PANELS, value, skipReSimulate)
                  }}
                  options={[
                    { label: t('common:yes'), value: true },
                    { label: t('common:no'), value: false }
                  ]}
                />
                {region === REGIONS.brussels && counterTypeToCheck === COUNTER_TYPES.analog && (
                  <InlineMessage className="mt-100" type="warning">
                    {t('simulation:steps.usage.form.noSolarPanelsBruAnalog')}
                  </InlineMessage>
                )}
              </div>
            </Form.Fieldset>

            {/* COUNTER TYPE */}
            <Form.Fieldset title={t('meterDetails.fields.counterType.label')}>
              <ToggleRadio
                grid="full"
                label={t('meterDetails.fields.counterType.label')}
                hiddenLabel
                name="counterType"
                disabled={isBoltDriveFlexFlow}
                message={
                  isBoltDriveFlexFlow
                    ? {
                        type: 'informative',
                        text: t('common:driveFlexWarnings.digitalMeter')
                      }
                    : undefined
                }
                options={[
                  {
                    ...register('counterType', {
                      onChange: (e) => handleCounterTypeChange(e.currentTarget.value)
                    }),
                    id: COUNTER_TYPES.digital,
                    label: t('common:yes'),
                    value: COUNTER_TYPES.digital
                  },
                  {
                    ...register('counterType', {
                      onChange: (e) => handleCounterTypeChange(e.currentTarget.value)
                    }),
                    id: COUNTER_TYPES.analog,
                    label: t('common:no'),
                    value: COUNTER_TYPES.analog
                  }
                ]}
              />
            </Form.Fieldset>
          </>
        )}

        {/* Check if eventID to know if it's a sales user that came from sales portal */}
        {canToggleSettlementFrequency && (
          <p className="grid-col-full">
            {t(
              `meterDetails.fields.counterType.descriptions.${watchSettlementFrequency?.toLowerCase() || SETTLEMENT_FREQUENCIES.yearly.toLowerCase()}`
            )}{' '}
            <Button
              representation="link"
              onClick={(e) => {
                e.preventDefault()
                setShowSettlementFrequencyModal(true)
              }}
            >
              {t('meterDetails.fields.counterType.clickHere')}
            </Button>
          </p>
        )}

        {/* DYNAMIC TARIFF */}
        {canToggleDynamicTariff && (
          <Form.Fieldset title={t('meterDetails.fields.dynamicTariff.label')}>
            <div className="grid-col-full">
              <Toggle
                active={!!dynamicTariff}
                id="dynamicTariff"
                isFullwidth
                disabled={isBoltDriveFlexFlow}
                onClick={(value) =>
                  saveAndResimulate(
                    InputGroupKeys.METER_DETAILS,
                    MeterDetailsFieldKeys.DYNAMIC_TARIFF,
                    value,
                    skipReSimulate || !hasSolarPanels
                  )
                }
                options={[
                  { label: t('common:yes'), value: true },
                  { label: t('common:no'), value: false }
                ]}
              />

              {isBoltDriveFlexFlow && (
                <InlineMessage className="mt-200" type="informative">
                  {t('common:driveFlexWarnings.dynamicTariff')}
                </InlineMessage>
              )}
            </div>

            <p className="grid-col-full">
              {t('meterDetails.fields.dynamicTariff.description')}{' '}
              <Link href={routes(locale).dynamicPricing} target="_blank">
                {t('common:readMore')}.
              </Link>
            </p>
          </Form.Fieldset>
        )}

        {!eanCodeMandatory && (
          <>
            <Form.Fieldset title={knowsMeterDetailsLabelFlag}>
              <div className="grid-col-full">
                <Toggle
                  active={!!watchKnowsMeterDetails}
                  id="knowsMeterDetails"
                  isFullwidth
                  onClick={(value) => setValue('knowsMeterDetails', value as never)}
                  options={[
                    { label: t('common:yes'), value: true },
                    { label: t('common:no'), value: false }
                  ]}
                />
              </div>

              {!watchKnowsMeterDetails && (
                <InlineMessage className="grid-col-full" type="informative">
                  {t('meterDetails.fields.knowsMeterDetails.dontKnow')}
                </InlineMessage>
              )}
            </Form.Fieldset>
          </>
        )}

        {watchKnowsMeterDetails && (
          <>
            {/* ELECTRICITY */}
            <Form.Fieldset title={t('meterDetails.sections.electricity')}>
              {usage.grade !== USAGE_GRADES.custom && (
                <CheckboxList>
                  {/* METER TYPE (single / double) */}
                  <Checkbox
                    checked={meterType === METER_TYPES.double}
                    id="hasDoubleRate"
                    label={t('simulation:steps.usage.form.hasDoubleRate')}
                    name="hasDoubleRate"
                    onChange={(e) =>
                      handleMeterDetailsCheckboxChanged(
                        InputGroupKeys.SIMULATION,
                        SimulationFieldKeys.METER_TYPE,
                        e.target.checked ? METER_TYPES.double : METER_TYPES.single
                      )
                    }
                  />

                  {/* EXCL NIGHT */}
                  <Checkbox
                    checked={hasExclusiveNightMeter}
                    id="hasExclusiveNightMeter"
                    label={t('simulation:steps.usage.form.hasExclNight')}
                    name="hasExclusiveNightMeter"
                    onChange={(e) =>
                      handleMeterDetailsCheckboxChanged(
                        InputGroupKeys.SIMULATION,
                        SimulationFieldKeys.HAS_EXCLUSIVE_NIGHT_METER,
                        e.target.checked
                      )
                    }
                  />

                  {/* WALLONIA SOLAR PANELS SINCE 2024 */}
                  {region === REGIONS.wallonia && counterTypeToCheck === COUNTER_TYPES.digital && (
                    <Checkbox
                      checked={walloniaSolarPanelsSince2024}
                      id="walloniaSolarPanelsSince2024"
                      label={t('common:walloniaSolarPanelsSince2024')}
                      name="walloniaSolarPanelsSince2024"
                      onChange={(e) =>
                        handleMeterDetailsCheckboxChanged(
                          InputGroupKeys.METER_DETAILS,
                          MeterDetailsFieldKeys.WALLONIA_SOLAR_PANELS_SINCE_2024,
                          e.target.checked
                        )
                      }
                    />
                  )}
                </CheckboxList>
              )}

              {/* ELECTRICITY EAN NUMBER */}
              <Ean
                {...(gasEanAtElectricityEanField && { errorMessage: t('commonFormFields:ean.errors.gasEanAtElectricityEanField') })}
                {...(invalidEan.el && { errorMessage: t('commonFormFields:ean.errors.invalid') })}
                {...(inputs.meterDetails.eanCaptureType === EAN_CAPTURE_TYPES.prefilled &&
                  inputs.meterDetails.electricity.ean !== null && {
                    message: t('meterDetails.fields.ean.prefilled')
                  })}
                onChange={() => {
                  setInvalidEan({ ...invalidEan, el: false })
                  setGasEanAtElectricityEanField(false)
                  updateEanCaptureTypeToOverwrittenIfNecessary()
                }}
                type={EanType.ELECTRICITY}
              />
              <p className="grid-col-full">{t('meterDetails.fields.ean.info')}</p>

              {showMeterReadAndNumberInputs && (
                <>
                  {meterType === METER_TYPES.single && <MeterReading fieldKey="total" parentDotPath="electricity" />}

                  {meterType === METER_TYPES.double && (
                    <>
                      <MeterReading fieldKey="day" parentDotPath="electricity" />

                      <MeterReading fieldKey="night" parentDotPath="electricity" />
                    </>
                  )}

                  <MeterNumber parentDotPath="electricity" />
                </>
              )}

              {/* EXCL NIGHT */}
              {hasExclusiveNightMeter && (
                <>
                  <Checkbox
                    checked={hasExclNightEan}
                    className="grid-col-full"
                    id="hasExclNightEan"
                    label={t('meterDetails.fields.hasExclNightEan.label')}
                    name="hasExclNightEan"
                    onChange={(e) => setHasExclNightEan(e.target.checked)}
                  />

                  {hasExclNightEan && (
                    <>
                      {/* ELECTRICITY EXCL. NIGHT EAN NUMBER */}
                      <Ean type={EanType.EXCL_NIGHT} />
                    </>
                  )}

                  {showMeterReadAndNumberInputs && <MeterReading fieldKey="exclNight" parentDotPath="electricity" />}
                </>
              )}

              {inputs.deliveryAddress.needsGas && (
                <Form.Fieldset title={t('meterDetails.sections.gas')}>
                  {/* GAS EAN NUMBER */}
                  <Ean
                    {...(electricityEanAtGasEanField && { errorMessage: t('commonFormFields:ean.errors.electricityEanAtGasEanField') })}
                    {...(invalidEan.gas && { errorMessage: t('commonFormFields:ean.errors.invalid') })}
                    {...(inputs.meterDetails.eanCaptureType === EAN_CAPTURE_TYPES.prefilled &&
                      inputs.meterDetails.gas.ean !== null && {
                        message: t('meterDetails.fields.ean.prefilled')
                      })}
                    onChange={() => {
                      setInvalidEan({ ...invalidEan, gas: false })
                      setElectricityEanAtGasEanField(false)
                      updateEanCaptureTypeToOverwrittenIfNecessary()
                    }}
                    type={EanType.GAS}
                  />

                  {showMeterReadAndNumberInputs && (
                    <>
                      <MeterReading fieldKey="total" parentDotPath="gas" />

                      <MeterNumber parentDotPath="gas" />
                    </>
                  )}
                </Form.Fieldset>
              )}
            </Form.Fieldset>
          </>
        )}

        <Form.Footer>
          <FormButtons {...{ isLoading }} />
        </Form.Footer>
      </Form>
    </FormProvider>
  )
}

export default MeterDetails
